import React, { useEffect, useState, useMemo } from 'react';
import {
  FiFileText,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiSearch,
  FiFilter,
  FiBriefcase,
  FiActivity
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  PageComponent,
  Skeletons,
  Table,
  TableActions,
  Modal,
  Select,
  MultiSelect
} from '../../components';
import SpecificDocumentModal from './SpecificDocumentModal';
import '../CompanyManager/style.css';
import './style.css';

export default function SpecificDocumentManager() {
  const permissions = usePermission('management');



  const [documents, setDocuments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (permissions.canView) {
      loadData();
    }
  }, [permissions.canView]);

  async function loadData() {
    try {
      setLoading(true);
      
      // Load companies and documents
      const [companiesData, documentsResponse] = await Promise.all([
        Api.getCompanies(),
        Api.getSpecificDocuments()
      ]);

      if (Array.isArray(companiesData)) {
        setCompanies(companiesData);
      }

      const docs = documentsResponse.data || documentsResponse;

      if (Array.isArray(docs)) {
        setDocuments(docs);
      } else {
        setDocuments([]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }

  const handleSave = async (data) => {
    try {
      if (selectedDocument) {
        // Update
        const response = await Api.updateSpecificDocument(selectedDocument.id, data);
        const updatedDoc = response.data || response;
        
        setDocuments(documents.map(d => 
          d.id === updatedDoc.id ? updatedDoc : d
        ));
        toast.success('Documento atualizado com sucesso');
      } else {
        // Create
        const response = await Api.createSpecificDocument(data);
        const newDoc = response.data || response;
        
        setDocuments([newDoc, ...documents]);
        toast.success('Documento criado com sucesso');
      }
      
      setIsModalOpen(false);
      setSelectedDocument(null);
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Erro ao salvar documento');
    }
  };

  const handleEdit = (doc) => {
    setSelectedDocument(doc);
    setIsModalOpen(true);
  };

  const handleRequestDelete = (doc) => {
    setDocumentToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    
    try {
      setIsDeleting(true);
      await Api.deleteSpecificDocument(documentToDelete.id);
      
      setDocuments(documents.filter(d => d.id !== documentToDelete.id));
      
      toast.success('Documento excluído com sucesso');
      setIsDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erro ao excluir documento');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!Array.isArray(documents)) return [];

    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);
    
    return documents.filter(doc => {
      if (!doc) return false;
      const title = doc.title || '';
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());

      const companyId = doc.company_id || doc.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [documents, searchTerm, companyFilterValues]);

  const companyOptions = useMemo(() => {
    return [
      { value: 'all', label: 'TODAS' },
      ...companies.map(c => ({
        value: String(c.id),
        label: c.corporate_name || c.trade_name || `Empresa ${c.id}`
      }))
    ];
  }, [companies]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyFilter(idsWithoutAll[0]);
    } else {
      setSelectedCompanyFilter('');
    }
  }

  const columns = [
    {
      id: 'title',
      header: 'Título',
      width: '1.5fr',
      cellClassName: 'manager-table-cell-primary',
      render: doc => (
        <>
          <div className="manager-table-avatar">
            <FiFileText size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{doc.title}</span>
            <span className="manager-table-subtitle">ID: {doc.id}</span>
          </div>
        </>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      render: doc => {
        const companyName = doc.company 
          ? (doc.company.corporate_name || doc.company.trade_name)
          : (companies.find(c => String(c.id) === String(doc.company_id))?.corporate_name || 'N/A');
          
        return (
          <div className="manager-table-details">
            <span className="manager-table-subtitle">
              {companyName}
            </span>
          </div>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Data de Criação',
      width: '1fr',
      render: doc => {
        const dateStr = doc.createdAt || doc.created_at;
        if (!dateStr) return <span>-</span>;
        try {
          return <span>{new Date(dateStr).toLocaleDateString()}</span>;
        } catch (e) {
          return <span>-</span>;
        }
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: doc => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(doc);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDelete(doc);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading ? (
          <Skeletons.CompanyManagerSkeleton />
        ) : (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiFileText size={22} />
                </div>
                <div>
                  <h1>Documentos Específicos</h1>
                  <p>Gerencie seus modelos de documentos</p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button 
                  onClick={() => {
                    setSelectedDocument(null);
                    setIsModalOpen(true);
                  }}
                  className="manager-header-button"
                >
                  <FiPlus size={18} />
                  Novo Documento
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
              </div>
              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    label="Empresa"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    placeholder="Todas as empresas"
                    icon={FiBriefcase}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      type="text"
                      placeholder="Buscar por título..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Table
              columns={columns}
              items={filteredDocuments}
              totalItems={filteredDocuments.length} 
              emptyMessage="Nenhum documento encontrado."
              onRowDoubleClick={permissions.canEdit ? handleEdit : undefined}
            />
          </>
        )}
      </div>

      <SpecificDocumentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDocument(null);
        }}
        onSave={handleSave}
        initialData={selectedDocument}
        companies={companies}
      />

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        width="400px"
      >
        <div className="delete-modal-content">
          <p>Tem certeza que deseja excluir o documento <strong>{documentToDelete?.title}</strong>?</p>
          <p className="text-danger">Esta ação não pode ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            loading={isDeleting}
          >
            Excluir
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
